import { useCallback, useMemo, useState } from "react";
import { useFormik } from "formik";
import _ from "lodash";

import { AddIcon } from "icons";
import { colors } from "theme/palette";
import { handleInvalidRequest } from "utils/sdk";

import Autocomplete from "components/Autocomplete";
import Button from "components/Button";
import Dialog, {
  DialogActions,
  DialogContent,
  DialogTitle,
} from "components/Dialog";
import Stack from "components/Stack";
import Text from "components/Text";
import TextField from "components/TextField";
import toast from "components/Toast";

import { useDealTablesAvailableSheetList } from "entities/Deal/sdk";
import { tableImportCreate } from "entities/TableStorage/sdk";

import { VALIDATION_SCHEMA } from "./constants";
import { COLUMN_OPTIONS, generateSheetOptions, ROW_OPTIONS } from "./utils";

interface IFormValues {
  table_name: string;
  sheet_name: string;
  column_start: string;
  column_end: string;
  row_start: number;
  row_end: number;
}

const initialValues: IFormValues = {
  table_name: "",
  sheet_name: "",
  column_start: "",
  column_end: "",
  row_start: 1,
  row_end: 10,
};

export interface ITableImportCreateDialogButton {
  dealId: number;
  createCallback?: () => void;
}

const TableImportCreateDialogButton = ({
  dealId,
  createCallback,
}: ITableImportCreateDialogButton) => {
  const [showDialog, setShowDialog] = useState<boolean>(false);
  const { data: sheets } = useDealTablesAvailableSheetList({
    dealId,
    enabled: showDialog,
  });

  const sheetOptions = useMemo(
    () => generateSheetOptions({ sheets }),
    [sheets]
  );

  const formik = useFormik<IFormValues>({
    initialValues: { ...initialValues },
    validationSchema: VALIDATION_SCHEMA,
    onSubmit: (values) => {
      tableImportCreate({ values, dealId })
        .then(() => {
          toast.successMessage("Table import created successfully");
          close();
          createCallback && createCallback();
        })
        .catch(handleInvalidRequest);
    },
  });

  const close = useCallback(() => {
    setShowDialog(false);
    formik.resetForm();
  }, [formik, setShowDialog]);

  return (
    <>
      <Button
        startIcon={<AddIcon />}
        onClick={() => setShowDialog(true)}
        sx={{ width: "180px" }}
        data-testid="create-table-button"
      >
        Create new table
      </Button>

      <Dialog
        fullWidth
        maxWidth="lg"
        open={showDialog}
        onClose={() => setShowDialog(false)}
        dialogDataTestid="table-import-create-dialog"
      >
        <DialogTitle
          sx={{
            margin: 2,
            borderRadius: "8px",
            backgroundColor: colors.blue20,
          }}
        >
          <Text variant="h2" color={colors.gray100}>
            Create new table import
          </Text>
        </DialogTitle>

        <DialogContent sx={{ overflowY: "visible" }}>
          <Text variant="text3" fontWeight={500}>
            Data range (cell range)
          </Text>
          <Text variant="text4" color={colors.gray60} marginBottom={2}>
            Select the sheet and cell range that will get synced.
          </Text>

          <Stack direction="row" spacing={2}>
            <Stack sx={{ flexBasis: "35%" }} data-testid="sheet-select">
              <Text variant="text3" fontWeight={500}>
                Sheet
              </Text>
              <Autocomplete
                {...formik.getFieldProps("sheet_name")}
                disableClearable
                options={sheetOptions}
                size="medium"
                selectOnFocus
                onChange={(event, value) => {
                  if (!_.isNil(value)) {
                    formik.setFieldValue("sheet_name", value.value);
                  }
                }}
                renderInput={(params) => <TextField {...params} label="" />}
                sx={{ background: colors.gray10 }}
              />
            </Stack>
            <Stack sx={{ flexBasis: "15%" }} data-testid="column-start-select">
              <Text variant="text3" fontWeight={500}>
                Column: START
              </Text>
              <Autocomplete
                {...formik.getFieldProps("column_start")}
                disableClearable
                options={COLUMN_OPTIONS}
                size="medium"
                selectOnFocus
                onChange={(event, value) => {
                  if (!_.isNil(value)) {
                    formik.setFieldValue("column_start", value);
                  }
                }}
                renderInput={(params) => <TextField {...params} label="" />}
                sx={{ background: colors.gray10 }}
              />
            </Stack>
            <Stack sx={{ flexBasis: "15%" }} data-testid="column-end-select">
              <Text variant="text3" fontWeight={500}>
                Column: END
              </Text>
              <Autocomplete
                {...formik.getFieldProps("column_end")}
                disableClearable
                options={COLUMN_OPTIONS}
                size="medium"
                selectOnFocus
                onChange={(event, value) => {
                  if (!_.isNil(value)) {
                    formik.setFieldValue("column_end", value);
                  }
                }}
                renderInput={(params) => <TextField {...params} label="" />}
                sx={{ background: colors.gray10 }}
              />
            </Stack>
            <Stack sx={{ flexBasis: "15%" }} data-testid="row-start-select">
              <Text variant="text3" fontWeight={500}>
                Row: START
              </Text>
              <Autocomplete
                {...formik.getFieldProps("row_start")}
                disableClearable
                options={ROW_OPTIONS}
                size="medium"
                selectOnFocus
                onChange={(event, value) => {
                  if (!_.isNil(value)) {
                    formik.setFieldValue("row_start", value);
                  }
                }}
                getOptionLabel={(option) => {
                  return _.isNumber(option) ? option.toString() : option;
                }}
                renderInput={(params) => <TextField {...params} label="" />}
                sx={{ background: colors.gray10 }}
              />
            </Stack>
            <Stack sx={{ flexBasis: "15%" }} data-testid="row-end-select">
              <Text variant="text3" fontWeight={500}>
                Row: END
              </Text>
              <Autocomplete
                {...formik.getFieldProps("row_end")}
                disableClearable
                options={ROW_OPTIONS}
                size="medium"
                selectOnFocus
                onChange={(event, value) => {
                  if (!_.isNil(value)) {
                    formik.setFieldValue("row_end", value);
                  }
                }}
                getOptionLabel={(option) => {
                  return _.isNumber(option) ? option.toString() : option;
                }}
                renderInput={(params) => <TextField {...params} label="" />}
                sx={{ background: colors.gray10 }}
              />
            </Stack>
          </Stack>
          <Stack sx={{ marginTop: 2 }}>
            <Text variant="text3" fontWeight={500}>
              Table name
            </Text>
            <Text variant="text4" color={colors.gray60}>
              Give it a name that will help you identify the data this table
              showcases.
            </Text>
            <TextField
              InputLabelProps={{ shrink: true }}
              {...formik.getFieldProps("table_name")}
              error={
                formik.touched.table_name &&
                !_.isEmpty(formik.errors.table_name)
              }
              helperText={formik.touched.table_name && formik.errors.table_name}
              sx={{ width: "100%", background: colors.gray10 }}
              slotProps={{
                htmlInput: {
                  "data-testid": "table-name-input",
                },
              }}
            />
          </Stack>
        </DialogContent>

        <DialogActions sx={{ padding: 2 }}>
          <Button
            variant="text"
            onClick={() => setShowDialog(false)}
            data-testid="cancel-button"
          >
            Cancel
          </Button>
          <Button
            color="primary"
            onClick={formik.submitForm}
            disabled={formik.isSubmitting}
            data-testid="create-table-import-button"
          >
            Create table import
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default TableImportCreateDialogButton;
